import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import Chip from "components/Chip/Chip";
import { Search, X } from "lucide-react";
import React, { useMemo, useRef, useState } from "react";
import {
  ROW_HEIGHT,
  VIEWPORT_HEIGHT,
} from "../constants/SelectedUsersBarConstants";
import { SelectedUsersBarProps } from "../types/ISelectedUsersBar";

const SelectedUsersBar: React.FC<SelectedUsersBarProps> = ({
  selectedUsers,
  onRemove,
  onClearAll,
  t,
  maxVisible = 4,
  disabled = false,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const virtualizedUsers = selectedUsers
    ?.slice()
    ?.sort(({ userName: a }, { userName: b }) =>
      a?.toLowerCase()?.localeCompare(b?.toLowerCase())
    );

  const visibleInline = virtualizedUsers?.slice(0, maxVisible);
  const remaining = Math.max(
    virtualizedUsers?.length - visibleInline?.length,
    0
  );

  const filteredUsers = useMemo(() => {
    let filtered = virtualizedUsers;

    if (searchTerm.trim()) {
      const searchLower = searchTerm?.toLowerCase()?.trim();
      filtered = filtered?.filter((user) =>
        user?.userName?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [virtualizedUsers, searchTerm]);

  return (
    <>
      <HStack spacing={3} wrap="wrap">
        {visibleInline.map((user) => (
          <Chip
            label={user?.userName}
            onClose={disabled ? undefined : () => onRemove(user?.userId)}
          />
        ))}
        {remaining > 0 && (
          <Button
            size="sm"
            variant="outline"
            onClick={onOpen}
            isDisabled={disabled}
          >
            +{remaining}
          </Button>
        )}
      </HStack>

      <Modal
        isCentered
        isOpen={isOpen}
        onClose={() => {
          onClose();
          setSearchTerm("");
        }}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="black">{t("selectedUsers")}</ModalHeader>
          <ModalCloseButton color="gray.500" />
          <ModalBody>
            <InputGroup width="100%" mb={4}>
              <InputLeftElement
                pointerEvents="none"
                children={<Search size="15px" color="#717680" />}
              />
              <Input
                id="searchField"
                color="#3A3A3A"
                bg="white"
                placeholder={t("searchByUserName") as string}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
              />
              {searchTerm?.length > 0 && (
                <InputRightElement
                  cursor={"pointer"}
                  onClick={() => setSearchTerm("")}
                  children={<X size="15px" color="#717680" />}
                />
              )}
            </InputGroup>
            <Flex
              flexWrap={"wrap"}
              alignItems={"flex-start"}
              justifyContent={"flex-start"}
              gap={2}
              ref={containerRef}
              maxHeight={`${VIEWPORT_HEIGHT}px`}
              overflowY="auto"
            >
              {filteredUsers?.map((user) => (
                <Box
                  key={user?.userId}
                  height={`${ROW_HEIGHT}px`}
                  display="flex"
                  alignItems="center"
                  px={1}
                >
                  <Chip
                    label={user?.userName}
                    onClose={
                      disabled ? undefined : () => onRemove(user?.userId)
                    }
                  />
                </Box>
              ))}
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              color="newBlue.500"
              mr={3}
              isDisabled={disabled}
              onClick={() => {
                onClose();
                onClearAll();
                setSearchTerm("");
              }}
            >
              {t("clear")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default SelectedUsersBar;
